class PromotionPopup extends HTMLElement {
  constructor() {
    super();
    this.popup = this.closest('.modal--popup');
    this.timeToShow = parseInt(this.getAttribute('data-delay')) || 0;
    // 遮罩层是本元素的兄弟节点（在 .modal--popup 里、promotion-popup 之外），
    // 用 this.popup.querySelector 取的是后代，取不到。
    this.overlay = this.parentElement?.querySelector('[id^="Drawer-Overlay-"]');
    this.content = this.popup.querySelector('.popup__inner');

    // 本地修改（客户要求）：弹过一次后 24 小时内不再弹。
    // 旧行为是「每次打开页面 / 跳转店铺链接都重新弹」，客户反馈太骚扰。
    // 键名带 storageKey 命名空间，和留客弹窗各记各的。
    this.storageNs = this.dataset.storageKey || 'default';
    this.storageKey = `_gl_promotion_popup_shown_${this.storageNs}`;
    this.expiresHours = Number(this.dataset.expireHours) || 24;
    this._storage = this.getBestStorage();

    this.querySelector('.popup__close')?.addEventListener('click', this.dismiss.bind(this));
    this.overlay?.addEventListener('click', this.dismiss.bind(this));

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.matches('.section-promotion-popup')) this.setOpenPopup(0);
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target.matches('.section-promotion-popup')) this.setClosePopup();
      });
    }
  }

  connectedCallback() {
    try { this.load(); } catch (_) { this.setOpenPopup(this.timeToShow); }
  }

  load() {
    this.observeEmailSuccess();
    this.watchDrawer();

    const hasSuccess = this.querySelector('.email-signup__message--success');
    const hasMessage = this.querySelector('.email-signup__message');

    if (hasSuccess) {
      if (hasMessage) this.popup.classList.add('active');
      if (!Shopify.designMode) this.setOpenPopup(0);
      return;
    }

    // 本地修改（客户要求）：弹过一次后 24 小时内不再弹。
    if (hasMessage) this.popup.classList.add('active');
    if (!Shopify.designMode && !this.getRecentShown()) {
      this.armScrollTrigger();
    }
  }

  observeEmailSuccess() {
    const target = this.querySelector('form') || this;
    this.successObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList.contains('email-signup__message--success')) {
            this.popup.classList.add('active');
            this.markShown();
            this.setOpenPopup(0);
          }
        });
      });
    });
    this.successObserver.observe(target, { childList: true, subtree: true });
  }

  // 本地修改（2026-11-02 客户要求）：改为滚动到页面 30% 再弹，不再用倒计时。
  // 阈值可用 data-scroll-depth 覆盖（0.3 = 30%）。
  armScrollTrigger() {
    if (this._scrollArmed) return;
    this._scrollArmed = true;
    const threshold = parseFloat(this.dataset.scrollDepth) || 0.3;
    let fired = false;
    const check = () => {
      if (fired) return;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      if (window.scrollY / max >= threshold) {
        fired = true;
        window.removeEventListener('scroll', check);
        window.removeEventListener('resize', check);
        if (!this.getRecentShown()) this.setOpenPopup(0);
      }
    };
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    check();
  }

  setOpenPopup(timeToShow) {
    setTimeout(() => {
      if (this.popup.hasAttribute('open')) return;
      // 本地修改（2026-10-28）：抽屉开着就不弹，避免弹窗盖住购物车结账按钮
      if (this.isBlockedByDrawer()) return;
      // 弹过就记账，避免用户没点关闭直接跳走时新页面又弹
      this.markShown();
      this.popup.setAttribute('open', 'true');
      this.animateOpen();
      document.body.classList.add('overflow-hidden');
      document.documentElement.setAttribute('scroll-lock', '');

      this.popup.addEventListener('transitionend', () => {
        const containerToTrapFocusOn = this.content;
        const focusElement = this.popup.querySelector('.field__input');
        trapFocus(containerToTrapFocusOn, focusElement);
      }, { once: true });
    }, timeToShow);
  }

  // 本地修改（2026-10-28）：抽屉（购物车/菜单等）打开时弹窗必须避让
  isBlockedByDrawer() {
    return !!document.querySelector('cart-drawer.open, cart-drawer.active, cart-drawer[open], side-drawer[open], .drawer.open');
  }

  // 抽屉一打开就把已经弹出的弹窗收起
  watchDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(() => {
      if (!this.popup.hasAttribute('open')) return;
      if (!this.isBlockedByDrawer()) return;
      this.closeQuietly();
    }).observe(drawer, { attributes: true, attributeFilter: ['class', 'open'] });
  }

  // 同 tempClose，但只有在没有别的抽屉打开时才解除滚动锁，否则会把购物车的滚动锁一起摘掉
  closeQuietly() {
    this.animateClose();
    setTimeout(() => {
      this.popup.removeAttribute('open');
      if (!this.isBlockedByDrawer()) {
        document.body.classList.remove('overflow-hidden');
        document.documentElement.removeAttribute('scroll-lock');
      }
      removeTrapFocus(this.activeElement);
    }, 100);
  }

  // 记下「刚弹过」的到期时间戳，N 小时内不再弹
  markShown() {
    try {
      if (Shopify.designMode || !this._storage) return;
      const until = Date.now() + this.expiresHours * 60 * 60 * 1000;
      this._storage.setItem(this.storageKey, JSON.stringify({ v: '1', exp: until }));
    } catch (_) {}
  }

  getRecentShown() {
    try {
      if (!this._storage) return null;
      const raw = this._storage.getItem(this.storageKey);
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (!payload || typeof payload.exp !== 'number') return null;
      if (Date.now() > payload.exp) {
        this._storage.removeItem(this.storageKey);
        return null;
      }
      return payload.v;
    } catch (_) { return null; }
  }

  getBestStorage() {
    try {
      const k = '__glpromo_test__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (_) {}
    try {
      const k2 = '__glpromo_test__';
      window.sessionStorage.setItem(k2, '1');
      window.sessionStorage.removeItem(k2);
      return window.sessionStorage;
    } catch (_) {}
    return null;
  }

  setClosePopup() {
    this.animateClose();
    setTimeout(() => {
      this.popup.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.documentElement.removeAttribute('scroll-lock');
      removeTrapFocus(this.activeElement);
    }, 100);
  }

  animateOpen() {
    Motion.timeline([
      [this.content, { opacity: [0, 1], transform: ['translate(-50%, -40%)', 'translate(-50%, -50%)'] }, { duration: 0.3, at: '-0.15' }]
    ]).finished;
  }

  animateClose() {
    Motion.timeline([
      [this.content, { opacity: [1, 0], transform: ['translate(-50%, -50%)', 'translate(-50%, -40%)'] }, { duration: 0.2 }]
    ]).finished;
  }

  tempClose() {
    this.animateClose();
    setTimeout(() => {
      this.popup.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.documentElement.removeAttribute('scroll-lock');
      removeTrapFocus(this.activeElement);
    }, 100);
  }

  dismiss() {
    // 本地修改：不再记录「多久之后才再显示」，关掉就只是关掉。
    this.tempClose();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

}
if (!customElements.get('promotion-popup')) customElements.define('promotion-popup', PromotionPopup);